import React, { useState, useEffect } from 'react';
import { format, addDays, isToday, addHours } from 'date-fns';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { API_ENDPOINTS } from '../constants/api';
import { useNavigate } from 'react-router-dom';

interface Availability {
  d: string;
  h: Array<{ h: number; a: number }>;
}

interface AvailabilityMap {
  [date: string]: { [hour: number]: number };
}

interface BookingFormData {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  postalCode: string;
  notes: string;
}

// Add new interface for postal code validation
interface PostalCodeValidationProps {
  onValidPostalCode: (postalCode: string) => void;
}

// Add new interface for the email modal
interface EmailModalProps {
  city: string;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (email: string) => void;
}

// New Postal Code Validation Component
// Add VideoIntro component
const VideoIntro: React.FC<{ onContinue: () => void }> = ({ onContinue }) => {
  return (
    <div className="max-w-4xl mx-auto my-8 px-4">
      <div className="relative mb-4">
        <div className="bg-green-50 rounded-lg p-4 border-l-4 border-custom-green shadow-md transform transition-all duration-300 hover:scale-[1.02] hover:shadow-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-custom-red bg-opacity-20 flex items-center justify-center">
                <svg className="w-6 h-6 text-custom-green animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="text-left">
                <p className="font-semibold text-gray-800">High Demand Alert!</p>
                <p className="text-sm text-gray-600">Only 3 spots left for this week's farm-fresh deliveries</p>
              </div>
            </div>
            <div className="hidden sm:block">
              <div className="bg-custom-red bg-opacity-10 px-4 py-2 rounded-full">
                <span className="text-custom-green font-medium text-sm">⚡ Limited Availability</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="relative aspect-video rounded-2xl overflow-hidden shadow-xl">
        <video
          autoPlay
          loop
          playsInline
          className="w-full h-full object-cover"
          src="/video.mp4"
        />
        <div className="absolute inset-0 bg-black bg-opacity-30" />
        <div className="absolute inset-x-0 bottom-8 flex justify-center">
          <button
            onClick={onContinue}
            className="px-8 py-4 bg-custom-red text-white rounded-full font-bold text-xl hover:bg-opacity-90 transition-all transform hover:scale-105 shadow-lg animate-bounce"
          >
            Book Now !
          </button>
        </div>
      </div>
    </div>
  );
};

const PostalCodeValidation: React.FC<PostalCodeValidationProps> = ({ onValidPostalCode }) => {
  const [postalCode, setPostalCode] = useState('');
  const [validationStatus, setValidationStatus] = useState<'none' | 'valid' | 'invalid' | 'toofar'>('none');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [cityName, setCityName] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const navigate = useNavigate();

  const validatePostalCode = async (code: string) => {
    try {
      setIsLoading(true);
      setErrorMessage('');
      const response = await fetch(`${API_ENDPOINTS.POSTAL}?code=${code.replace(/\s/g, '')}`);
      const data = await response.json();

      if (data.error === "Postal code not found") {
        setValidationStatus('invalid');
        setErrorMessage('This postal code does not exist');
      } else if (data.error === "Too far") {
        setValidationStatus('toofar');
        setCityName(data.city);
        setShowEmailModal(true);
      } else if (data.status === "ok") {
        setValidationStatus('valid');
      }
    } catch (error) {
      setValidationStatus('invalid');
      setErrorMessage('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePostalCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.toUpperCase();
    // Remove any spaces first
    value = value.replace(/\s/g, '');
    
    // Add space after first 3 characters if length > 3
    if (value.length > 3) {
      value = value.slice(0, 3) + ' ' + value.slice(3);
    }
    
    // Only allow up to 7 characters (including space)
    if (value.length <= 7) {
      setPostalCode(value);
      
      // Reset validation if empty
      if (value.length === 0) {
        setValidationStatus('none');
        return;
      }

      // If we have 6 characters (excluding space), validate
      if (value.replace(/\s/g, '').length === 6) {
        if (/^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/.test(value.replace(/\s/g, ''))) {
          validatePostalCode(value);
        } else {
          setValidationStatus('invalid');
        }
      } else {
        setValidationStatus('none');
      }
    }
  };

  const handleEmailSubmit = async (email: string) => {
    try {
      setIsLoading(true);
      const response = await fetch(`${API_ENDPOINTS.EMAIL_OTHER_REGION}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          postal_code: postalCode.replace(/\s/g, ''),
          email: email
        })
      });

      if (!response.ok) {
        throw new Error('Failed to submit email');
      }

      setShowEmailModal(false);
      setShowSuccessMessage(true);
    } catch (error) {
      console.error('Error submitting email:', error);
      setErrorMessage('Failed to submit email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (showSuccessMessage) {
    return (
      <div className="max-w-2xl mx-auto text-center py-12">
        <div className="bg-green-50 rounded-2xl p-8 border border-green-100">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-custom-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">
            Thank you for your interest!
          </h3>
          <p className="text-gray-600 mb-8">
            We'll notify you as soon as we start delivering to your area.
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-8 py-3 bg-custom-red text-white rounded-full font-bold hover:bg-opacity-90 transition-colors text-lg"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto text-center">
      <h2 className="text-3xl font-bold text-gray-900 mb-4">
        Let's check if we deliver to your area
      </h2>
      <p className="text-gray-600 mb-8">
        Please enter your postal code.
      </p>
      
      <div className="relative mb-6">
        <div className="relative">
          <input
            type="text"
            value={postalCode}
            onChange={handlePostalCodeChange}
            placeholder="XXX XXX"
            className={`w-full px-4 py-3 border-2 rounded-lg text-lg text-center uppercase tracking-wider
              ${validationStatus === 'valid' 
                ? 'border-custom-green focus:border-custom-green focus:ring-custom-green' 
                : validationStatus === 'invalid' || validationStatus === 'toofar'
                  ? 'border-red-500 focus:border-red-500 focus:ring-red-500' 
                  : 'border-gray-300 focus:border-custom-green focus:ring-custom-green'}`}
            maxLength={7}
          />
          {validationStatus === 'valid' && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <svg className="w-6 h-6 text-custom-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          )}
          {(validationStatus === 'invalid' || validationStatus === 'toofar') && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
          )}
          {errorMessage && (
            <p className="mt-2 text-sm text-red-500">
              {errorMessage}
            </p>
          )}
        </div>
      </div>

      <button
        onClick={() => validationStatus === 'valid' && onValidPostalCode(postalCode)}
        disabled={validationStatus !== 'valid' || isLoading}
        className={`px-8 py-3 rounded-full font-bold text-lg transition-colors ${
          validationStatus === 'valid' && !isLoading
            ? 'bg-custom-red text-white hover:bg-opacity-90'
            : 'bg-gray-200 text-gray-400 cursor-not-allowed'
        }`}
      >
        {isLoading ? 'Verifying...' : 'Continue'}
      </button>

      <EmailModal
        city={cityName}
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        onSubmit={handleEmailSubmit}
      />

      <div className="mt-12 -mx-4 sm:-mx-6 lg:-mx-8">
        <img 
          src="/delivery-illustration.svg" 
          alt="Delivery illustration" 
          className="w-full h-auto"
        />
      </div>
    </div>
  );
};

// Add new interface for step items
interface StepItem {
  number: number;
  label: string;
  status: 'completed' | 'current' | 'upcoming';
}

// Add new Steps component
const BookingSteps: React.FC<{ currentStep: number; submitSuccess?: boolean }> = ({ currentStep, submitSuccess }) => {
  const steps: StepItem[] = [
    {
      number: 1,
      label: 'Welcome',
      status: currentStep > 1 ? 'completed' : currentStep === 1 ? 'current' : 'upcoming'
    },
    {
      number: 2,
      label: 'Address',
      status: currentStep > 2 ? 'completed' : currentStep === 2 ? 'current' : 'upcoming'
    },
    {
      number: 3,
      label: 'Preferences',
      status: currentStep > 3 ? 'completed' : currentStep === 3 ? 'current' : 'upcoming'
    },
    {
      number: 4,
      label: 'Your informations',
      status: currentStep > 4 ? 'completed' : currentStep === 4 ? 'current' : 'upcoming'
    },
    {
      number: 5,
      label: 'Book a meet !',
      status: submitSuccess ? 'completed' : currentStep === 5 ? 'current' : 'upcoming'
    }
  ];

  return (
    <div className="fixed top-24 left-0 right-0 bg-white z-50 border-b">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="overflow-x-auto scrollbar-hide">
          <div className="flex justify-center min-w-max py-4">
            {steps.map((step, index) => (
              <div key={step.number} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 
                    ${step.status === 'completed' ? 'bg-custom-red border-custom-green' : 
                      step.status === 'current' ? 'border-custom-green bg-white' : 
                      'border-gray-300 bg-white'}`}>
                    {step.status === 'completed' ? (
                      <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <span className={`text-sm font-medium
                        ${step.status === 'current' ? 'text-custom-green' : 'text-gray-500'}`}>
                        {step.number}
                      </span>
                    )}
                  </div>
                  <span className={`mt-2 text-sm whitespace-nowrap
                    ${step.status === 'completed' ? 'text-custom-green' :
                      step.status === 'current' ? 'text-custom-green font-medium' :
                      'text-gray-500'}`}>
                    {step.label}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div className={`w-24 h-0.5 mx-2
                    ${step.status === 'completed' ? 'bg-custom-red' : 'bg-gray-300'}`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const EmailModal: React.FC<EmailModalProps> = ({ city, isOpen, onClose, onSubmit }) => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    await onSubmit(email);
    setIsSubmitting(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-modal="true">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="fixed inset-0 bg-black bg-opacity-75 transition-opacity" onClick={onClose}></div>
        <div className="relative bg-white rounded-lg p-8 max-w-md w-full">
          <h3 className="text-xl font-bold text-gray-900 mb-4">
            We're not there yet!
          </h3>
          <p className="text-gray-600 mb-6">
            We do not deliver to <b>{city}</b> yet. Leave your email and we'll inform you when we start delivering to your area!
          </p>
          <form onSubmit={handleSubmit}>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email"
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg mb-4 focus:ring-custom-green focus:border-custom-green"
            />
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-600 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2 bg-custom-red text-white rounded-lg hover:bg-opacity-90 disabled:opacity-50"
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

interface FamilySizeFormProps {
  onSubmit: (familySize: number, mealsPerWeek: number, categories: string[]) => void;
  initialFamilySize?: number;
  initialMealsPerWeek?: number;
  initialCategories?: string[];
}

interface Category {
  id: string;
  name: string;
}

const FamilySizeForm: React.FC<FamilySizeFormProps> = ({
  onSubmit,
  initialFamilySize = 2,
  initialMealsPerWeek = 4,
  initialCategories = []
}) => {
  const [familySize, setFamilySize] = useState(initialFamilySize);
  const [mealsPerWeek, setMealsPerWeek] = useState(initialMealsPerWeek);
  const [selectedCategories, setSelectedCategories] = useState<string[]>(initialCategories);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(API_ENDPOINTS.MAIN_CATEGORIES);
        if (!response.ok) {
          throw new Error('Failed to fetch categories');
        }
        const data = await response.json();
        setCategories(data);
      } catch (err) {
        setError('Failed to load categories');
        console.error('Error fetching categories:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCategories();
  }, []);

  const toggleCategory = (categoryId: string) => {
    setSelectedCategories(prev => 
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  return (
    <div className="max-w-2xl mx-auto text-center">
      <h2 className="text-3xl font-bold text-gray-900 mb-4">
        Customize your Box size
      </h2>
      <p className="text-gray-600 mb-12">
        This Box will be registered as your default choice, but you can always modify it each week.
      </p>

      <div className="space-y-12">
        <div>
          <label className="block text-left text-lg font-medium text-gray-700 mb-4">
            Family size
          </label>
          <div className="grid grid-cols-5 gap-2">
            {[1, 2, 3, 4, '5+'].map((size) => (
              <button
                key={size}
                onClick={() => setFamilySize(typeof size === 'string' ? 5 : size)}
                className={`py-4 text-center rounded-lg border-2 transition-colors
                  ${familySize === (typeof size === 'string' ? 5 : size)
                    ? 'border-custom-green bg-custom-red text-white'
                    : 'border-gray-300 text-gray-700 hover:border-custom-green'
                  }`}
              >
                {size}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-left text-lg font-medium text-gray-700 mb-4">
            Protein Meals per week
          </label>
          <div className="grid grid-cols-5 gap-2">
            {[2, 3, 4, 5, 6].map((meals) => (
              <button
                key={meals}
                onClick={() => setMealsPerWeek(meals)}
                className={`py-4 text-center rounded-lg border-2 transition-colors
                  ${mealsPerWeek === meals
                    ? 'border-custom-green bg-custom-red text-white'
                    : 'border-gray-300 text-gray-700 hover:border-custom-green'
                  }`}
              >
                {meals === 6 ? '6+' : meals}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-left text-lg font-medium text-gray-700 mb-4">
            Select your preferred categories
          </label>
          {isLoading ? (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-custom-green"></div>
            </div>
          ) : error ? (
            <div className="text-red-500 text-center py-4">{error}</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => toggleCategory(category.id)}
                  className={`flex items-center px-4 py-3 rounded-lg border-2 transition-colors
                    ${selectedCategories.includes(category.id)
                      ? 'border-custom-green bg-white text-custom-green'
                      : 'border-gray-300 text-gray-700 hover:border-custom-green'
                    }`}
                >
                  <div className={`w-5 h-5 border-2 rounded mr-3 flex items-center justify-center
                    ${selectedCategories.includes(category.id)
                      ? 'border-custom-green bg-custom-red'
                      : 'border-gray-300'
                    }`}
                  >
                    {selectedCategories.includes(category.id) && (
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  {category.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <button
        onClick={() => onSubmit(familySize, mealsPerWeek, selectedCategories)}
        className="mt-12 px-8 py-3 bg-custom-red text-white rounded-full font-bold hover:bg-opacity-90 transition-colors text-lg"
      >
        Continue
      </button>
    </div>
  );
};

const Booking = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<BookingFormData>({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    postalCode: '',
    notes: ''
  });
  const [availabilities, setAvailabilities] = useState<AvailabilityMap>({});
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [familySize, setFamilySize] = useState<number>(0);
  const [mealsPerWeek, setMealsPerWeek] = useState<number>(0);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  const TIMEZONE = 'America/Toronto';

  // Get current time in Eastern Time (UTC-4)
  const getCurrentEasternTime = () => {
    const now = new Date();
    // Adjust for Eastern Time (UTC-4)
    return addHours(now, -4);
  };

  // Generate all time slots from 6 AM to 10 PM
  const timeSlots = Array.from({ length: 17 }, (_, i) => {
    const hour = i + 6; // Start from 6 AM
    return `${hour.toString().padStart(2, '0')}:00`;
  });

  // Add this constant at the component level for consistent button styling
  const buttonClasses = "px-8 py-3 bg-custom-red text-white rounded-full font-bold hover:bg-opacity-90 transition-colors text-lg";

  useEffect(() => {
    const fetchData = async () => {
      // Handle the view API request first
      const lastRequestDate = localStorage.getItem('lastViewRequest');
      const currentDate = new Date().toISOString();

      const urlParams = new URLSearchParams(window.location.search);
      const infid = urlParams.get('infid');

      if (!lastRequestDate || (lastRequestDate && new Date() > addDays(new Date(lastRequestDate), 7))) {
        try {
          const url = new URL(API_ENDPOINTS.VIEW);
          if (infid) {
            url.searchParams.append('infid', infid);
          }

          await fetch(url.toString(), { method: 'POST' });
          localStorage.setItem('lastViewRequest', currentDate);
          if (infid) {
            localStorage.setItem('infid', infid);
          }

          if (infid) {
            urlParams.delete('infid');
            const newUrl = urlParams.toString()
              ? `${window.location.pathname}?${urlParams.toString()}`
              : window.location.pathname;
            window.history.replaceState({}, '', newUrl);
          }
        } catch (error) {
          console.error('Error making view request:', error);
        }
      }

      // Fetch availabilities
      try {
        const response = await fetch(API_ENDPOINTS.AVAILABILITIES, {
          method: 'POST'
        });
        const data: Availability[] = await response.json();

        const availMap: AvailabilityMap = {};
        const easternTime = getCurrentEasternTime();
        const currentHour = easternTime.getHours();

        data.forEach(({ d, h }) => {
          availMap[d] = {};
          h.forEach(({ h: hour, a: available }) => {
            if (d === format(easternTime, 'yyyy-MM-dd') && hour <= currentHour) {
              availMap[d][hour] = 0;
            } else {
              availMap[d][hour] = available;
            }
          });
        });

        // Set available dates (only dates with at least one available time slot)
        const availableDatesList = Object.keys(availMap).filter(
          date => Object.values(availMap[date]).some(available => available > 0)
        );

        setAvailabilities(availMap);
        setAvailableDates(availableDatesList);

        // If current selected date has no availability, reset selection
        const formattedSelectedDate = format(selectedDate, 'yyyy-MM-dd');
        if (!availableDatesList.includes(formattedSelectedDate)) {
          setSelectedDate(new Date(availableDatesList[0]));
          setSelectedTime(null);
        }
      } catch (error) {
        console.error('Error fetching availabilities:', error);
      }
    };

    fetchData();
  }, []); // Run once on mount

  const isTimeSlotAvailable = (time: string, date: Date) => {
    const formattedDate = format(date, 'yyyy-MM-dd');
    const hour = parseInt(time.split(':')[0], 10);
    const dateAvailabilities = availabilities[formattedDate] || {};

    const easternTime = getCurrentEasternTime();
    const easternHour = easternTime.getHours();
    const easternDate = format(easternTime, 'yyyy-MM-dd');

    if (formattedDate === easternDate && hour <= easternHour) {
      return false;
    }

    return (dateAvailabilities[hour] || 0) > 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, ''); // Remove non-digits
    if (value.length <= 10) { // Only allow up to 10 digits
      setFormData(prev => ({
        ...prev,
        phone: value
      }));
    }
  };

  const handleFamilySizeSubmit = (size: number, meals: number, categories: string[]) => {
    setFamilySize(size);
    setMealsPerWeek(meals);
    setSelectedCategories(categories);
    setCurrentStep(4);
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentStep(5);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (formData.phone.length !== 10) {
      setSubmitError('Please enter a valid 10-digit phone number');
      return;
    }

    const infid = localStorage.getItem('infid');
    const reservationDate = format(selectedDate, 'yyyy-MM-dd');

    try {
      const requestBody = {
          infid: infid || null,
          clientFirstName: formData.firstName,
          clientLastName: formData.lastName,
          clientEmail: formData.email,
          clientPhone: `+1${formData.phone}`,
          postalCode: formData.postalCode,
          familySize: familySize.toString(),
          mealsWithProteinPerWeek: mealsPerWeek,
          categoryIds: selectedCategories,
          reservationDate: reservationDate,
          reservationTime: selectedTime,
          notes: formData.notes || null,
          timezone: TIMEZONE
      };
      console.log('Reservation request body:', requestBody);

      const response = await fetch(API_ENDPOINTS.RESERVATION, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (response.status === 201) {
        setSubmitSuccess(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        // Optional: Clear form data
        setFormData({
          firstName: '',
          lastName: '',
          phone: '',
          email: '',
          postalCode: '',
          notes: ''
        });
      } else {
        const errorData = await response.json();
        setSubmitError(errorData.error || 'An error occurred while submitting the reservation');
      }
    } catch (error) {
      setSubmitError('Network error occurred. Please try again.');
    }
  };

  return (
    <>
      <style>
        {`
          /* Override default calendar styles */
          .react-calendar {
            border: none;
            box-shadow: none;
            width: 100%;
          }
          
          /* Override weekend colors */
          .react-calendar__month-view__days__day--weekend {
            color: #111827 !important;  /* text-gray-900 */
          }
          
          /* Override today's highlight */
          .react-calendar__tile--now {
            background: #d1fae5 !important;  /* Light mint green */
          }
          
          .react-calendar__tile--now:enabled:hover,
          .react-calendar__tile--now:enabled:focus {
            background: #a7f3d0 !important;  /* Slightly darker mint green on hover/focus */
          }
          
          /* Selected date */
          .react-calendar__tile--active {
            background: #10B981 !important;  /* custom-green */
            color: white !important;
          }
          
          .react-calendar__tile--active:enabled:hover,
          .react-calendar__tile--active:enabled:focus {
            background: #10B981 !important;
          }
          
          /* Hide scrollbar but maintain functionality */
          .scrollbar-hide {
            -ms-overflow-style: none;  /* IE and Edge */
            scrollbar-width: none;  /* Firefox */
          }
          .scrollbar-hide::-webkit-scrollbar {
            display: none;  /* Chrome, Safari and Opera */
          }
        `}
      </style>
      {currentStep !== 1 && <BookingSteps currentStep={currentStep} submitSuccess={submitSuccess} />}
      <div className={currentStep === 1 ? '' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative mt-16'}>
        {currentStep === 1 && (
          <VideoIntro onContinue={() => setCurrentStep(2)} />
        )}
        {currentStep === 2 && (
          <>
            <div className="max-w-2xl mx-auto mb-8">
              <div className="bg-green-50 rounded-lg p-4 border-l-4 border-custom-green shadow-md transform transition-all duration-300 hover:scale-[1.02] hover:shadow-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-full bg-custom-red bg-opacity-20 flex items-center justify-center">
                      <svg className="w-6 h-6 text-custom-green animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-gray-800">High Demand Alert!</p>
                      <p className="text-sm text-gray-600">Only 3 spots left for this week's farm-fresh deliveries</p>
                    </div>
                  </div>
                  <div className="hidden sm:block">
                    <div className="bg-custom-red bg-opacity-10 px-4 py-2 rounded-full">
                      <span className="text-custom-green font-medium text-sm">⚡ Limited Availability</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <PostalCodeValidation
              onValidPostalCode={(code) => {
                setFormData(prev => ({
                  ...prev,
                  postalCode: code
                }));
                setCurrentStep(3);
              }}
            />
          </>
        )}
        {currentStep === 3 && (
         <>
           <div className="max-w-2xl mx-auto mb-8">
             <div className="bg-green-50 rounded-lg p-4 border-l-4 border-custom-green shadow-md transform transition-all duration-300 hover:scale-[1.02] hover:shadow-lg">
               <div className="flex items-center justify-between">
                 <div className="flex items-center space-x-3">
                   <div className="w-10 h-10 rounded-full bg-custom-red bg-opacity-20 flex items-center justify-center">
                     <svg className="w-6 h-6 text-custom-green animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                     </svg>
                   </div>
                   <div className="text-left">
                     <p className="font-semibold text-gray-800">High Demand Alert!</p>
                     <p className="text-sm text-gray-600">Only 3 spots left for this week's farm-fresh deliveries</p>
                   </div>
                 </div>
                 <div className="hidden sm:block">
                   <div className="bg-custom-red bg-opacity-10 px-4 py-2 rounded-full">
                     <span className="text-custom-green font-medium text-sm">⚡ Limited Availability</span>
                   </div>
                 </div>
               </div>
             </div>
           </div>
           <FamilySizeForm
             onSubmit={handleFamilySizeSubmit}
             initialFamilySize={familySize}
             initialMealsPerWeek={mealsPerWeek}
             initialCategories={selectedCategories}
           />
         </>
        )}
        {currentStep === 4 && (
         <>
           <div className="max-w-2xl mx-auto mb-8">
             <div className="bg-green-50 rounded-lg p-4 border-l-4 border-custom-green shadow-md transform transition-all duration-300 hover:scale-[1.02] hover:shadow-lg">
               <div className="flex items-center justify-between">
                 <div className="flex items-center space-x-3">
                   <div className="w-10 h-10 rounded-full bg-custom-red bg-opacity-20 flex items-center justify-center">
                     <svg className="w-6 h-6 text-custom-green animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                     </svg>
                   </div>
                   <div className="text-left">
                     <p className="font-semibold text-gray-800">High Demand Alert!</p>
                     <p className="text-sm text-gray-600">Only 3 spots left for this week's farm-fresh deliveries</p>
                   </div>
                 </div>
                 <div className="hidden sm:block">
                   <div className="bg-custom-red bg-opacity-10 px-4 py-2 rounded-full">
                     <span className="text-custom-green font-medium text-sm">⚡ Limited Availability</span>
                   </div>
                 </div>
               </div>
             </div>
           </div>
           <form onSubmit={handleFormSubmit} className="max-w-2xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">Personal Information</h2>
            <p className="text-gray-600 mb-8 text-center">
              Please provide your contact information for the delivery.
            </p>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                  First Name
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-custom-green focus:border-custom-green"
                />
              </div>
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-custom-green focus:border-custom-green"
                />
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <div className="relative flex items-center">
                  <div className="absolute left-4 flex items-center space-x-1 pointer-events-none">
                    <img 
                      src="/icons/canada.svg" 
                      alt="Canada" 
                      className="w-5 h-5"
                    />
                    <span className="text-gray-500 font-medium">+1</span>
                  </div>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    required
                    pattern="[0-9]{10}"
                    className="w-full px-4 py-2 pl-24 border border-gray-300 rounded-lg focus:ring-custom-green focus:border-custom-green"
                    placeholder="1234567890"
                    maxLength={10}
                  />
                </div>
                {formData.phone.length > 0 && formData.phone.length !== 10 && (
                  <p className="mt-1 text-sm text-red-500">
                    Please enter a valid 10-digit phone number
                  </p>
                )}
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-custom-green focus:border-custom-green"
                />
              </div>
              <div>
                <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                  Additional Notes (Optional)
                </label>
                <textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-custom-green focus:border-custom-green"
                  placeholder="Any special requests or additional information..."
                />
              </div>
            </div>

            <div className="flex justify-center space-x-4 mt-8">
              <button
                type="button"
                onClick={() => setCurrentStep(3)}
                className="px-8 py-3 border border-gray-300 rounded-full font-bold text-gray-700 hover:bg-gray-50 transition-colors text-lg"
              >
                Back
              </button>
              <button
                type="submit"
                className={buttonClasses}
              >
                Next
              </button>
            </div>
          </form>
         </>
        )}
        {currentStep === 5 && (
         <>
           <div className="max-w-2xl mx-auto mb-8">
             <div className="bg-green-50 rounded-lg p-4 border-l-4 border-custom-green shadow-md transform transition-all duration-300 hover:scale-[1.02] hover:shadow-lg">
               <div className="flex items-center justify-between">
                 <div className="flex items-center space-x-3">
                   <div className="w-10 h-10 rounded-full bg-custom-red bg-opacity-20 flex items-center justify-center">
                     <svg className="w-6 h-6 text-custom-green animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                     </svg>
                   </div>
                   <div className="text-left">
                     <p className="font-semibold text-gray-800">High Demand Alert!</p>
                     <p className="text-sm text-gray-600">Only 3 spots left for this week's farm-fresh deliveries</p>
                   </div>
                 </div>
                 <div className="hidden sm:block">
                   <div className="bg-custom-red bg-opacity-10 px-4 py-2 rounded-full">
                     <span className="text-custom-green font-medium text-sm">⚡ Limited Availability</span>
                   </div>
                 </div>
               </div>
             </div>
           </div>
           <div className="bg-white rounded-lg p-6 w-full max-w-5xl mx-auto shadow-sm">
            {submitSuccess ? (
              <div className="max-w-2xl mx-auto text-center py-12">
                <div className="bg-green-50 rounded-2xl p-8 border border-green-100">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg
                      className="w-8 h-8 text-custom-green"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    Reservation submitted successfully!
                  </h3>
                  <p className="text-gray-600 mb-8">
                    Thank you for your reservation. We'll see you on {format(selectedDate, 'MMMM d, yyyy')} at {selectedTime}.
                  </p>
                  <button
                    onClick={() => {
                      setShowForm(false);
                      setShowBookingForm(false);
                      setSubmitSuccess(false);
                      setSelectedDate(new Date());
                      setSelectedTime(null);
                      navigate('/');
                    }}
                    className={buttonClasses}
                  >
                    Back to Home
                  </button>
                </div>
              </div>
            ) : (
              <>
                <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">Select Date and Time</h2>
                <p className="text-gray-600 mb-8 text-center">
                  Choose your preferred delivery date and time slot for your farm-fresh products.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Calendar Section */}
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4">Select Date</h3>
                    <Calendar
                      onChange={(date) => {
                        if (date instanceof Date) {
                          setSelectedDate(date);
                          setSelectedTime(null);
                        }
                      }}
                      value={selectedDate}
                      minDate={new Date()}
                      tileDisabled={({ date }) => !availableDates.includes(format(date, 'yyyy-MM-dd'))}
                      formatShortWeekday={(locale, date) => format(date, 'EEEEE')}
                    />
                  </div>

                  {/* Time Slots Section */}
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4">Select Time</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {timeSlots.map((time) => {
                        const isAvailable = isTimeSlotAvailable(time, selectedDate);
                        return (
                          <button
                            key={time}
                            onClick={() => isAvailable && setSelectedTime(time)}
                            disabled={!isAvailable}
                            className={`
                              py-2 px-4 rounded-lg border text-sm font-medium transition-colors
                              ${selectedTime === time
                                ? 'bg-custom-red text-white border-custom-green'
                                : isAvailable
                                  ? 'border-gray-300 text-gray-700 hover:border-custom-green'
                                  : 'border-gray-200 text-gray-400 bg-gray-100 cursor-not-allowed'
                              }
                            `}
                          >
                            {time}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="flex justify-center space-x-4 mt-8">
                  <button
                    type="button"
                    onClick={() => setCurrentStep(4)}
                    className="px-8 py-3 border border-gray-300 rounded-full font-bold text-gray-700 hover:bg-gray-50 transition-colors text-lg"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={!selectedDate || !selectedTime}
                    className={`${buttonClasses} ${(!selectedDate || !selectedTime) ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    Submit
                  </button>
                </div>
              </>
            )}
          </div>
         </>
        )}
      </div>
    </>
  );
};

export default Booking;